import { useState, type ReactNode } from 'react';
import { sha256OfFile } from '../lib/api';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status string any of the three backend modules' APIs
// actually return (see src/lib/types.ts for the full per-module enum list).
// Unmapped statuses still render, just in the neutral slate tone.
const STATUS_TONES: Record<string, string> = {
  // ── Shared baseline (fleet convention) ──────────────────────────────────
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GRANTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DONE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMPLETE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  RESOLVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  VERIFIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PUBLISHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CLEARED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FULFILLED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CONFIRMED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DECIDED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  VALIDATED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISBURSED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  AUTO_ELIGIBLE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  UPHELD_FOR_COMPLAINANT: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  DISMISSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  NONE: 'bg-slate-100 text-slate-500 ring-slate-200',
  LOW: 'bg-slate-100 text-slate-500 ring-slate-200',
  NO_FURTHER_ACTION: 'bg-slate-100 text-slate-500 ring-slate-200',
  WAIVED: 'bg-slate-100 text-slate-500 ring-slate-200',
  UPHELD_FOR_OPERATOR: 'bg-slate-100 text-slate-500 ring-slate-200',

  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SCREENING: 'bg-sky-50 text-sky-700 ring-sky-200',
  SCOPING: 'bg-sky-50 text-sky-700 ring-sky-200',
  FILED: 'bg-sky-50 text-sky-700 ring-sky-200',
  RECEIVED: 'bg-sky-50 text-sky-700 ring-sky-200',
  PENDING_OCR: 'bg-sky-50 text-sky-700 ring-sky-200',
  PENDING_COSIGN: 'bg-sky-50 text-sky-700 ring-sky-200',
  STANDBY: 'bg-sky-50 text-sky-700 ring-sky-200',
  QUEUED: 'bg-sky-50 text-sky-700 ring-sky-200',
  APPLIED: 'bg-sky-50 text-sky-700 ring-sky-200',
  ASSIGNED: 'bg-sky-50 text-sky-700 ring-sky-200',

  UNDER_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  EIS_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  TECHNICAL_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  CONSULTATION: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  IN_PROGRESS: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  INVESTIGATING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  INSTALLER_COSIGNED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  REFERRALS_PENDING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  MANUAL_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  NEEDS_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ACKNOWLEDGED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  CONTAINED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ACCEPTED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  PUC_HANDED_OFF: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  PUBLIC_COMMENT: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  DETERMINED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  COMMISSIONED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  OCR_MATCHED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  USED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',

  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',
  OVERDUE: 'bg-amber-50 text-amber-700 ring-amber-200',
  MODERATE: 'bg-amber-50 text-amber-700 ring-amber-200',
  OBJECTED: 'bg-amber-50 text-amber-700 ring-amber-200',
  MAJOR: 'bg-amber-50 text-amber-700 ring-amber-200',
  SEVERE: 'bg-amber-50 text-amber-700 ring-amber-200',
  PARTIALLY_UPHELD: 'bg-amber-50 text-amber-700 ring-amber-200',

  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REFUSED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DESTROYED: 'bg-rose-50 text-rose-700 ring-rose-200',
  EXTREME: 'bg-rose-50 text-rose-700 ring-rose-200',
  HIGH: 'bg-rose-50 text-rose-700 ring-rose-200',
  DENIED: 'bg-rose-50 text-rose-700 ring-rose-200',
  OCR_MISMATCH: 'bg-rose-50 text-rose-700 ring-rose-200',
  REFERRED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M31 environment — EiaClass (screening rules ONE/TWO/THREE) ─────────
  ONE: 'bg-violet-50 text-violet-700 ring-violet-200',
  TWO: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  THREE: 'bg-slate-100 text-slate-600 ring-slate-200',
  HELD: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-200',
  APPROVED_WITH_CONDITIONS: 'bg-teal-50 text-teal-700 ring-teal-200',

  // ── M31 — AlertSeverity/DamageSeverity/HazardRiskLevel MINOR ────────────
  MINOR: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/** Inline notice shown when the signed-in persona lacks a role a screen's mutating actions require — reads are usually still visible, writes will 403 for real. */
export function RoleNotice({ roles }: { roles: string[] }) {
  return (
    <div className="mb-4">
      <ErrorNote message={`Your persona does not hold ${roles.join(' or ')} — actions on this screen will be rejected by the API (403). You can still view what's below.`} />
    </div>
  );
}

/** Label/value pair used throughout every workspace's detail modals. */
export function Detail({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

/** Compact tab strip used to switch between related views within one workspace screen. */
export function TabStrip<T extends string>({ tabs, active, onChange }: { tabs: Array<{ key: T; label: string }>; active: T; onChange: (key: T) => void }) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            active === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export interface FileHashValue {
  docRef: string;
  sha256: string;
  fileName: string;
}

/**
 * A file picker that computes a REAL SHA-256 over the picked file's actual
 * bytes via SubtleCrypto (see lib/api.ts's sha256OfFile) — every document
 * field across M31/M35/M36 (photos, invoices, single-line diagrams, site
 * plans, evidence) is a `{docRef, sha256}` pair, and this portal has no
 * object-storage upload endpoint of its own to actually persist the file
 * (out of scope — flagged in the build report), so `docRef` is set to the
 * file's own name rather than a real storage key. The digest itself is
 * never fabricated: it's a genuine hash of the genuine file the user picked.
 */
export function FileHashField({ label, onChange, required }: { label: string; onChange: (v: FileHashValue | null) => void; required?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<FileHashValue | null>(null);

  return (
    <Field label={`${label}${required ? ' *' : ''}`}>
      <input
        type="file"
        className="field-input file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-2.5 file:py-1 file:text-xs file:font-semibold"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) {
            setPicked(null);
            onChange(null);
            return;
          }
          setBusy(true);
          try {
            const sha256 = await sha256OfFile(file);
            const value = { docRef: file.name, sha256, fileName: file.name };
            setPicked(value);
            onChange(value);
          } finally {
            setBusy(false);
          }
        }}
      />
      {busy ? <p className="mt-1 text-xs text-slate-400">Computing SHA-256…</p> : null}
      {picked ? (
        <p className="mt-1 truncate font-mono text-[11px] text-slate-500" title={picked.sha256}>
          sha256 {picked.sha256.slice(0, 16)}…
        </p>
      ) : null}
    </Field>
  );
}
